import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { OnboardingV2State } from '../../store/onboardingV2/types.ts';
import { onboardingAgent, AgentQuestion, AgentMessage } from '../../services/onboardingAgent/index.ts';
import { OnboardingStep } from '../../utils/onboardingV2/conversationalOnboardingSteps.ts';

export interface Message {
  id: string;
  role: 'assistant' | 'user';
  content: string;
  timestamp: string;
  question?: Question | null;
}

export type Question = AgentQuestion;

interface UseConversationalOnboardingProps {
  state: OnboardingV2State;
  setAccountType: (type: 'PERSON' | 'COMPANY') => void;
  setPersonaForm: (formData: any) => void;
  setEmpresaForm: (formData: any) => void;
  setUserSkills: (skills: any[]) => void;
  addSkill: (skill: any) => void;
  removeSkill: (skillId: number) => void;
  setWorkZones: (zones: any[]) => void;
  setAllCountry: (allCountry: boolean) => void;
  setUserImage: (image: string | null) => void;
  setSelectedCategoryFilter: (categoryId: number | null) => void;
  setPreferredProfileCategories: (categoryIds: number[]) => void;
  removePreferredProfileCategory: (categoryId: number) => void;
  updateProfile: () => Promise<any>;
  loadSkillsAndCategories: () => Promise<any>;
  getCities: (provinceId: number) => Promise<any>;
}

export const useConversationalOnboarding = ({
  state,
  setAccountType,
  setPersonaForm,
  setEmpresaForm,
  setUserSkills,
  addSkill,
  removeSkill,
  setWorkZones,
  setAllCountry,
  setUserImage,
  setSelectedCategoryFilter,
  setPreferredProfileCategories,
  removePreferredProfileCategory,
  updateProfile,
  loadSkillsAndCategories,
  getCities,
}: UseConversationalOnboardingProps) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [completedMessageId, setCompletedMessageId] = useState<string | null>(null);
  const [showReturnToFinish, setShowReturnToFinish] = useState(false);
  const navigate = useNavigate();
  const initializedRef = useRef<boolean>(false);
  const pendingSkillResetRef = useRef<boolean>(false);
  const reviewModeRef = useRef<boolean>(false);
  const reviewFieldsRef = useRef<string[]>([]);
  const reviewIndexRef = useRef<number>(0);
  const pendingQuestionRef = useRef<Question | null>(null);
  const activeTypingMessageIdRef = useRef<string | null>(null);
  const typingTimeoutRef = useRef<number | null>(null);

  const lastAssistantMessage = messages.length > 0 
    ? messages.filter(m => m.role === 'assistant').pop()
    : null;

  const currentQuestion =
    lastAssistantMessage && lastAssistantMessage.id === completedMessageId
      ? lastAssistantMessage.question ?? null
      : null;
  const currentField = currentQuestion?.field ?? null;
  const currentFieldValue = currentField
    ? state.accountType === 'PERSON'
      ? (state.personaForm as any)[currentField]
      : (state.empresaForm as any)[currentField]
    : null;
  const isOptionalField = ['tel', 'webSite', 'bio'].includes(currentField ?? '');
  const canKeepRequiredField =
    ['name', 'lastName', 'companyName'].includes(currentField ?? '') && !!currentFieldValue;
  const shouldShowSkip =
    reviewModeRef.current ||
    (currentQuestion?.type === 'text' && (isOptionalField || canKeepRequiredField));
  const skipLabel = reviewModeRef.current || canKeepRequiredField ? 'Mantener' : 'Omitir';

  const hasProgress = useCallback(() => {
    return !!(
      state.accountType ||
      (state.accountType === 'PERSON' && (state.personaForm.name || state.personaForm.lastName || state.personaForm.tel)) ||
      (state.accountType === 'COMPANY' && (state.empresaForm.companyName || state.empresaForm.tel)) ||
      state.userSkills.length > 0 ||
      state.workZones.length > 0 ||
      state.allCountry
    );
  }, [state]);

  const validateRequiredOnboardingData = useCallback((currentState: OnboardingV2State) => {
    const hasSkills = currentState.userSkills.length > 0;
    const hasProfiles = (currentState.preferredProfileCategories?.length ?? 0) > 0;
    const hasZones = currentState.allCountry || currentState.workZones.length > 0;
    const valid = hasSkills && hasProfiles && hasZones;
    const missing: ('skills' | 'profiles' | 'zones')[] = [];
    if (!hasSkills) missing.push('skills');
    if (!hasProfiles) missing.push('profiles');
    if (!hasZones) missing.push('zones');
    return { valid, missing };
  }, []);

  const getValidationErrorMessage = useCallback((missing: ('skills' | 'profiles' | 'zones')[]) => {
    if (missing.length === 0) return '';
    const labels: Record<'skills' | 'profiles' | 'zones', string> = {
      skills: 'habilidades',
      profiles: 'categorías de perfiles con quien conectar',
      zones: 'zonas de trabajo (o "Todo el país")',
    };
    const list = missing.map((m) => labels[m]).join(', ');
    return missing.length === 1
      ? (missing[0] === 'skills' && 'Agregá al menos una habilidad para continuar.')
      || (missing[0] === 'profiles' && 'Seleccioná al menos una categoría de perfiles con quien conectar.')
      || (missing[0] === 'zones' && 'Seleccioná "Todo el país" o agregá al menos una zona de trabajo.')
      : `Para continuar completá: ${list}. Podés revisar el panel de la derecha.`;
  }, []);

  const getQuestionForMissingStep = useCallback((missing: 'skills' | 'profiles' | 'zones'): Question => {
    const map: Record<'skills' | 'profiles' | 'zones', Question> = {
      skills: { id: 'skills', type: 'interactive', interactiveType: 'skills' },
      profiles: { id: 'profiles', type: 'interactive', interactiveType: 'profiles' },
      zones: { id: 'workZones', type: 'interactive', interactiveType: 'workZones' },
    };
    return map[missing];
  }, []);
  
  const normalizeQuestion = useCallback((question?: Question | null): Question | null => {
    if (!question) return null;
    const normalizedField = question.field === 'description' ? 'bio' : question.field;
    const normalizedId = question.id === 'description' ? 'bio' : question.id;
    if (normalizedField === question.field && normalizedId === question.id) {
      return question;
    }
    return {
      ...question,
      field: normalizedField,
      id: normalizedId,
    };
  }, []);

  const addMessage = useCallback((message: Omit<Message, 'id' | 'timestamp'>) => {
    const newMessage: Message = {
      ...message,
      id: `msg-${Date.now()}-${Math.random()}`,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, newMessage]);
    return newMessage;
  }, []);

  const addAssistantMessage = useCallback((content: string, question?: Question | null, delay: number = 0) => {
    setTimeout(() => {
      const normalizedQuestion = normalizeQuestion(question ?? null);
      pendingQuestionRef.current = normalizedQuestion;
      setIsTyping(true);
      setCompletedMessageId(null);
      const createdMessage = addMessage({ role: 'assistant', content, question: normalizedQuestion });
      activeTypingMessageIdRef.current = createdMessage.id;
    }, delay);
  }, [addMessage, normalizeQuestion]);

  const beginAssistantTransition = useCallback(() => {
    setCompletedMessageId(null);
  }, []);

  const getBasicDataFields = useCallback((accountType: OnboardingV2State['accountType']) => {
    if (accountType === 'PERSON') {
      return ['name', 'lastName', 'tel', 'webSite', 'bio'];
    }
    if (accountType === 'COMPANY') {
      return ['companyName', 'tel', 'webSite', 'bio'];
    }
    return [];
  }, []);

  const getReviewMessage = useCallback((field: string, currentState: OnboardingV2State): AgentMessage => {
    const isPerson = currentState.accountType === 'PERSON';
    if (field === 'name') {
      const currentValue = currentState.personaForm.name;
      return {
        content: `¿Querés cambiar tu nombre? ${currentValue ? `Actual: ${currentValue}.` : ''}`,
        question: { id: 'name', type: 'text', field: 'name', placeholder: 'Escribí tu nombre...' },
      };
    }
    if (field === 'lastName') {
      const currentValue = currentState.personaForm.lastName;
      return {
        content: `¿Querés cambiar tu apellido? ${currentValue ? `Actual: ${currentValue}.` : ''}`,
        question: { id: 'lastName', type: 'text', field: 'lastName', placeholder: 'Escribí tu apellido...' },
      };
    }
    if (field === 'companyName') {
      const currentValue = currentState.empresaForm.companyName;
      return {
        content: `¿Querés cambiar el nombre de tu empresa? ${currentValue ? `Actual: ${currentValue}.` : ''}`,
        question: { id: 'companyName', type: 'text', field: 'companyName', placeholder: 'Escribí el nombre de tu empresa...' },
      };
    }
    if (field === 'tel') {
      const currentValue = isPerson ? currentState.personaForm.tel : currentState.empresaForm.tel;
      return {
        content: `¿Querés cambiar tu teléfono? ${currentValue ? `Actual: ${currentValue}.` : ''} Actualizalo o tocá "Mantener" para conservarlo.`,
        question: { id: 'tel', type: 'text', field: 'tel', placeholder: 'Escribí tu teléfono (solo números)...' },
      };
    }
    if (field === 'webSite') {
      const currentValue = isPerson ? currentState.personaForm.webSite : currentState.empresaForm.webSite;
      return {
        content: `¿Querés cambiar tu sitio web? ${currentValue ? `Actual: ${currentValue}.` : ''} Actualizalo o tocá "Mantener" para conservarlo.`,
        question: { id: 'webSite', type: 'text', field: 'webSite', placeholder: 'Escribí tu sitio web (opcional)...' },
      };
    }
    const currentValue = isPerson ? currentState.personaForm.bio : currentState.empresaForm.bio;
    return {
      content: `¿Querés cambiar tu descripción? ${currentValue ? `Actual: ${currentValue}.` : ''} Actualizala o tocá "Mantener" para conservarla.`,
      question: { id: 'bio', type: 'text', field: 'bio', placeholder: isPerson ? 'Contame sobre vos...' : 'Contame sobre tu empresa...' },
    };
  }, []);

  const handleTextResponse = useCallback((value: string, field: string) => {
    beginAssistantTransition();
    const form = state.accountType === 'PERSON' ? state.personaForm : state.empresaForm;
    const updateForm = state.accountType === 'PERSON' ? setPersonaForm : setEmpresaForm;

    updateForm({
      ...form,
      [field]: value,
    });

    addMessage({ role: 'user', content: value });

    setTimeout(async () => {
      const updatedForm = state.accountType === 'PERSON' 
        ? { ...state.personaForm, [field]: value }
        : { ...state.empresaForm, [field]: value };
      
      const updatedState = {
        ...state,
        [state.accountType === 'PERSON' ? 'personaForm' : 'empresaForm']: updatedForm,
      };

      if (reviewModeRef.current) {
        const nextIndex = reviewIndexRef.current + 1;
        if (nextIndex < reviewFieldsRef.current.length) {
          reviewIndexRef.current = nextIndex;
          const nextField = reviewFieldsRef.current[nextIndex];
          const reviewMessage = getReviewMessage(nextField, updatedState);
          addAssistantMessage(reviewMessage.content, reviewMessage.question, 300);
          return;
        }
        reviewModeRef.current = false;
        reviewFieldsRef.current = [];
        reviewIndexRef.current = 0;
        setIsReviewMode(false);
      }

      const context = {
        state: updatedState,
        messages: [...messages, { role: 'user' as const, content: value }],
        lastUserMessage: value,
        lastAction: `text_${field}`,
      };

      try {
        const agentResponse = await onboardingAgent.getResponse(context);
        addAssistantMessage(agentResponse.content, agentResponse.question, 300);
      } catch {
      }
    }, 200);
  }, [beginAssistantTransition, state, setPersonaForm, setEmpresaForm, addMessage, addAssistantMessage, messages, getReviewMessage]);

  const handleInteractiveAction = useCallback(async (action: string, data?: any) => {
    if (action === 'accountType') {
      const wasAccountTypeSet = !!state.accountType;
      beginAssistantTransition();
      setAccountType(data);
      addMessage({ role: 'user', content: data === 'PERSON' ? 'Profesional' : 'Empresa' });
      setTimeout(async () => {
        if (wasAccountTypeSet) {
          reviewFieldsRef.current = getBasicDataFields(data);
          reviewIndexRef.current = 0;
          reviewModeRef.current = reviewFieldsRef.current.length > 0;
          setIsReviewMode(reviewModeRef.current);
          if (reviewModeRef.current) {
            const reviewState = { ...state, accountType: data };
            const reviewMessage = getReviewMessage(reviewFieldsRef.current[0], reviewState);
            addAssistantMessage(reviewMessage.content, reviewMessage.question, 300);
            return;
          }
        }
        const updatedState = {
          ...state,
          accountType: data,
        };
        const context = {
          state: updatedState,
          messages: [...messages, { role: 'user' as const, content: data === 'PERSON' ? 'Profesional' : 'Empresa' }],
          lastUserMessage: data === 'PERSON' ? 'Profesional' : 'Empresa',
          lastAction: action,
        };
        try {
          const agentResponse = await onboardingAgent.getResponse(context);
          addAssistantMessage(agentResponse.content, agentResponse.question, 500);
        } catch {
        }
      }, 100);
    } else if (action === 'skill') {
      if (data.action === 'add') {
        addSkill(data.skill);
        pendingSkillResetRef.current = true;
      } else if (data.action === 'remove') {
        removeSkill(data.skillId);
        addMessage({ role: 'user', content: 'Removí la habilidad' });
      }
    } else if (action === 'profileCategory') {
      const current = state.preferredProfileCategories || [];
      const categoryId = data?.categoryId;
      if (typeof categoryId === 'number') {
        const isSelected = current.includes(categoryId);
        const updated = isSelected
          ? current.filter((id) => id !== categoryId)
          : [...current, categoryId];
        setPreferredProfileCategories(updated);
      }
    } else if (action === 'removeProfileCategory') {
      if (typeof data?.categoryId === 'number') {
        removePreferredProfileCategory(data.categoryId);
      }
    } else if (action === 'skillsComplete') {
      if (state.userSkills.length === 0) {
        addAssistantMessage('Agregá al menos una habilidad para continuar.', getQuestionForMissingStep('skills'), 300);
        setShowReturnToFinish(true);
        return;
      }
      setShowReturnToFinish(false);
      beginAssistantTransition();
      setTimeout(async () => {
        const context = {
          state,
          messages: [...messages, { role: 'user' as const, content: 'Continuar' }],
          lastUserMessage: 'Continuar',
          lastAction: action,
        };
        try {
          const agentResponse = await onboardingAgent.getResponse(context);
          addAssistantMessage(agentResponse.content, agentResponse.question, 500);
        } catch {
        }
      }, 100);
    } else if (action === 'profilesComplete') {
      if ((state.preferredProfileCategories?.length ?? 0) === 0) {
        addAssistantMessage('Seleccioná al menos una categoría de perfiles con quien conectar.', getQuestionForMissingStep('profiles'), 300);
        setShowReturnToFinish(true);
        return;
      }
      setShowReturnToFinish(false);
      beginAssistantTransition();
      setTimeout(async () => {
        const context = {
          state,
          messages: [...messages, { role: 'user' as const, content: 'Continuar' }],
          lastUserMessage: 'Continuar',
          lastAction: action,
        };
        try {
          const agentResponse = await onboardingAgent.getResponse(context);
          addAssistantMessage(agentResponse.content, agentResponse.question, 500);
        } catch {
        }
      }, 100);
    } else if (action === 'selectJurisdiction') {
      getCities(data.jurisdictionId);
    } else if (action === 'workZone') {
      if (data.action === 'allCountry') {
        setAllCountry(true);
        setWorkZones([]);
        addMessage({ role: 'user', content: 'Todo el país' });
        setTimeout(async () => {
          const updatedState = {
            ...state,
            allCountry: true,
            workZones: [],
          };
          const context = {
            state: updatedState,
            messages: [...messages, { role: 'user' as const, content: 'Todo el país' }],
            lastUserMessage: 'Todo el país',
            lastAction: action,
          };
          try {
            const agentResponse = await onboardingAgent.getResponse(context);
            addAssistantMessage(agentResponse.content, agentResponse.question, 300);
          } catch {
          }
        }, 100);
      } else if (data.action === 'specificZones') {
        setAllCountry(false);
      } else if (data.action === 'add') {
        setAllCountry(false);
        setWorkZones([...state.workZones, data.zone]);
        addMessage({ role: 'user', content: `Agregué: ${data.zone.city || data.zone.name}` });
      } else if (data.action === 'remove') {
        const updatedZones = state.workZones.filter((z: any, index: number) => {
          if (data.zoneId === undefined || data.zoneId === null) {
            return true;
          }
          if (typeof data.zoneId === 'number') {
            return z.id !== data.zoneId && index !== data.zoneId;
          }
          return z.id !== data.zoneId;
        });
        setWorkZones(updatedZones);
        addMessage({ role: 'user', content: 'Removí la zona' });
      }
    } else if (action === 'workZonesComplete') {
      if (!state.allCountry && state.workZones.length === 0) {
        addAssistantMessage('Seleccioná "Todo el país" o agregá al menos una zona de trabajo.', getQuestionForMissingStep('zones'), 300);
        setShowReturnToFinish(true);
        return;
      }
      setShowReturnToFinish(false);
      beginAssistantTransition();
      const context = {
        state,
        messages: [...messages, { role: 'user' as const, content: 'Continuar' }],
        lastUserMessage: 'Continuar',
        lastAction: action,
      };
      try {
        const agentResponse = await onboardingAgent.getResponse(context);
        addAssistantMessage(agentResponse.content, agentResponse.question, 500);
      } catch {
      }
    } else if (action === 'finishOnboarding') {
      const finishValidation = validateRequiredOnboardingData(state);
      if (!finishValidation.valid) {
        const firstMissing = finishValidation.missing[0];
        addAssistantMessage(getValidationErrorMessage(finishValidation.missing), getQuestionForMissingStep(firstMissing), 300);
        setShowReturnToFinish(true);
        return;
      }
      beginAssistantTransition();
      const context = {
        state,
        messages: [...messages, { role: 'user' as const, content: 'Finalizar onboarding' }],
        lastUserMessage: 'Finalizar onboarding',
        lastAction: action,
      };
      try {
        const agentResponse = await onboardingAgent.getResponse(context);
        addAssistantMessage(agentResponse.content, agentResponse.question, 300);
      } catch {
      }
    } else if (action === 'summaryEdit') {
      beginAssistantTransition();
      const context = {
        state,
        messages: [...messages, { role: 'user' as const, content: 'Editar pasos' }],
        lastUserMessage: 'Editar pasos',
        lastAction: action,
      };
      try {
        const agentResponse = await onboardingAgent.getResponse(context);
        addAssistantMessage(agentResponse.content, agentResponse.question, 300);
      } catch {
      }
    } else if (action === 'returnToCompleteActions') {
      setShowReturnToFinish(false);
      beginAssistantTransition();
      addAssistantMessage(
        'Podés revisar o cambiar pasos, o finalizar el onboarding para guardar tu perfil.',
        { id: 'completeActions', type: 'interactive', interactiveType: 'completeActions' },
        300
      );
    } else if (action === 'confirmSummary') {
      const summaryValidation = validateRequiredOnboardingData(state);
      if (!summaryValidation.valid) {
        const firstMissing = summaryValidation.missing[0];
        addAssistantMessage(getValidationErrorMessage(summaryValidation.missing), getQuestionForMissingStep(firstMissing), 300);
        setShowReturnToFinish(true);
        return;
      }
      setIsProcessing(true);
      addMessage({ role: 'user', content: 'Confirmar y finalizar' });
      try {
        await updateProfile();
        navigate('/dashboard', { state: { showProfilePostModal: true } });
      } catch {
      } finally {
        setIsProcessing(false);
      }
    }
  }, [beginAssistantTransition, state, setAccountType, addSkill, removeSkill, setWorkZones, setAllCountry, addMessage, addAssistantMessage, getCities, messages, updateProfile, setPreferredProfileCategories, removePreferredProfileCategory, navigate, getBasicDataFields, getReviewMessage, validateRequiredOnboardingData, getValidationErrorMessage, getQuestionForMissingStep]);

  const handleSendMessage = useCallback(() => {
    if (!inputValue.trim() || isProcessing || !currentQuestion) return;

    const value = inputValue.trim();
    setInputValue('');

    if (currentQuestion.type === 'text' && currentQuestion.field) {
      setIsProcessing(true);
      handleTextResponse(value, currentQuestion.field);
      setTimeout(() => {
        setIsProcessing(false);
      }, 500);
    }
  }, [inputValue, isProcessing, currentQuestion, handleTextResponse]);

  const handleSkipField = useCallback(async () => {
    if (!currentQuestion || currentQuestion.type !== 'text' || !currentQuestion.field) return;
    
    const optionalFields = ['tel', 'webSite', 'bio'];
    const keepableFields = ['name', 'lastName', 'companyName'];
    const isKeepable =
      keepableFields.includes(currentQuestion.field) &&
      !!currentFieldValue;
    if (!reviewModeRef.current && !optionalFields.includes(currentQuestion.field) && !isKeepable) return;

    setIsProcessing(true);
    beginAssistantTransition();
    setInputValue('');

    const form = state.accountType === 'PERSON' ? state.personaForm : state.empresaForm;
    const shouldKeep = isKeepable || reviewModeRef.current;
    const updatedForm = shouldKeep
      ? form
      : { ...form, [currentQuestion.field]: null };
    if (!shouldKeep) {
      const updateForm = state.accountType === 'PERSON' ? setPersonaForm : setEmpresaForm;
      updateForm(updatedForm);
    }
    const updatedState = {
      ...state,
      [state.accountType === 'PERSON' ? 'personaForm' : 'empresaForm']: updatedForm,
    };
    const userLabel = shouldKeep ? 'Mantener' : 'Omitir';
    addMessage({ role: 'user', content: userLabel });

    if (reviewModeRef.current) {
      const nextIndex = reviewIndexRef.current + 1;
      if (nextIndex < reviewFieldsRef.current.length) {
        reviewIndexRef.current = nextIndex;
        const nextField = reviewFieldsRef.current[nextIndex];
        const reviewMessage = getReviewMessage(nextField, updatedState);
        addAssistantMessage(reviewMessage.content, reviewMessage.question, 300);
        setIsProcessing(false);
        return;
      }
      reviewModeRef.current = false;
      reviewFieldsRef.current = [];
      reviewIndexRef.current = 0;
      setIsReviewMode(false);
    }

    const context = {
      state: updatedState,
      messages: [...messages, { role: 'user' as const, content: userLabel }],
      lastUserMessage: userLabel,
      lastAction: `${shouldKeep ? 'keep' : 'skip'}_${currentQuestion.field}`,
    };

    try {
      const agentResponse = await onboardingAgent.getResponse(context);
      addAssistantMessage(agentResponse.content, agentResponse.question, 300);
    } catch {
    } finally {
      setIsProcessing(false);
    }
  }, [beginAssistantTransition, currentQuestion, state, setPersonaForm, setEmpresaForm, addMessage, addAssistantMessage, messages, getReviewMessage, currentFieldValue]);

  useEffect(() => {
    if (messages.length === 0 && !initializedRef.current) {
      initializedRef.current = true;
      loadSkillsAndCategories();
      const hasProgressData = hasProgress();
      const currentState = state;
      setTimeout(async () => {
        try {
          let agentMessage: AgentMessage;
          if (currentState.isFromSkipOnboarding) {
            agentMessage = {
              content: 'Antes de finalizar, revisá el resumen para confirmar que todo esté correcto.',
              question: {
                id: 'summary',
                type: 'interactive',
                interactiveType: 'summary',
              },
            };
          } else if (hasProgressData) {
            agentMessage = await onboardingAgent.getResumeMessage(currentState);
          } else {
            agentMessage = await onboardingAgent.getWelcomeMessage();
          }
          
          addAssistantMessage(agentMessage.content, agentMessage.question, 0);
        } catch {
          setIsTyping(false);
        }
      }, 300);
    }
  }, [addAssistantMessage, loadSkillsAndCategories, hasProgress, messages.length, state]);

  const navigationPendingRef = useRef<boolean>(false);
  const navigationTargetRef = useRef<OnboardingStep | null>(null);

  const getNavigationMessage = useCallback((targetStep: OnboardingStep, currentState: OnboardingV2State): AgentMessage => {
    if (!currentState.accountType) {
      return {
        content: 'Primero necesito saber qué tipo de cuenta querés crear. Seleccioná una opción.',
        question: {
          id: 'accountType',
          type: 'interactive',
          interactiveType: 'accountType',
        },
      };
    }

    if (targetStep === 'accountType') {
      return {
        content: 'Podés cambiar el tipo de cuenta si lo necesitás. Seleccioná una opción.',
        question: {
          id: 'accountType',
          type: 'interactive',
          interactiveType: 'accountType',
        },
      };
    }

    if (targetStep === 'basicData') {
      const fields = reviewFieldsRef.current.length > 0
        ? reviewFieldsRef.current
        : getBasicDataFields(currentState.accountType);
      if (fields.length > 0) {
        reviewFieldsRef.current = fields;
        reviewIndexRef.current = 0;
        reviewModeRef.current = true;
        setIsReviewMode(true);
        return getReviewMessage(fields[0], currentState);
      }
    }

    if (targetStep === 'skills') {
      if (currentState.userSkills.length === 0) {
        return {
          content: 'Podés revisar o cambiar tus habilidades. Buscá y agregá las que correspondan.',
          question: {
            id: 'skills',
            type: 'interactive',
            interactiveType: 'skills',
          },
        };
      }
      if (currentState.preferredProfileCategories.length === 0) {
        return {
          content: 'Ahora elegí con qué perfiles querés conectar. Seleccioná las categorías que te interesan.',
          question: {
            id: 'profiles',
            type: 'interactive',
            interactiveType: 'profiles',
          },
        };
      }
      return {
        content: 'Podés revisar o cambiar tus habilidades y perfiles. Buscá y ajustá lo que necesites.',
        question: {
          id: 'skills',
          type: 'interactive',
          interactiveType: 'skills',
        },
      };
    }

    if (targetStep === 'workZones') {
      return {
        content: 'Podés revisar o cambiar tus zonas de trabajo. Seleccioná una opción.',
        question: {
          id: 'workZones',
          type: 'interactive',
          interactiveType: 'workZones',
        },
      };
    }

    if (targetStep === 'summary') {
      return {
        content: 'Revisá tu perfil antes de confirmar.',
        question: {
          id: 'summary',
          type: 'interactive',
          interactiveType: 'summary',
        },
      };
    }

    return {
      content: 'Podés revisar o cambiar pasos, o finalizar el onboarding para guardar tu perfil.',
      question: {
        id: 'completeActions',
        type: 'interactive',
        interactiveType: 'completeActions',
      },
    };
  }, [getBasicDataFields, getReviewMessage]);

  useEffect(() => {
    if (navigationPendingRef.current && messages.length === 0 && !isTyping && !isProcessing) {
      navigationPendingRef.current = false;
      const hasProgressData = hasProgress();
      setTimeout(async () => {
        try {
          let agentMessage: AgentMessage;
          if (navigationTargetRef.current) {
            agentMessage = getNavigationMessage(navigationTargetRef.current, state);
            navigationTargetRef.current = null;
          } else if (state.isFromSkipOnboarding) {
            agentMessage = {
              content: 'Antes de finalizar, revisá el resumen para confirmar que todo esté correcto.',
              question: {
                id: 'summary',
                type: 'interactive',
                interactiveType: 'summary',
              },
            };
          } else if (hasProgressData) {
            agentMessage = await onboardingAgent.getResumeMessage(state);
          } else {
            agentMessage = await onboardingAgent.getWelcomeMessage();
          }
          
          addAssistantMessage(agentMessage.content, agentMessage.question, 0);
        } catch {
          setIsTyping(false);
        }
      }, 300);
    }
  }, [messages.length, isTyping, isProcessing, hasProgress, state, addAssistantMessage, getNavigationMessage]);

  const navigateToStep = useCallback((targetStep: OnboardingStep) => {
    const lastMsg = messages.length > 0 ? messages.filter((m: Message) => m.role === 'assistant').pop() : null;
    if (lastMsg?.question?.interactiveType === 'completeActions') {
      setShowReturnToFinish(true);
    }
    setMessages([]);
    initializedRef.current = false;
    navigationPendingRef.current = true;
    navigationTargetRef.current = targetStep;
    if (targetStep === 'basicData') {
      reviewFieldsRef.current = getBasicDataFields(state.accountType);
      reviewIndexRef.current = 0;
      reviewModeRef.current = reviewFieldsRef.current.length > 0;
      setIsReviewMode(reviewModeRef.current);
    } else {
      reviewFieldsRef.current = [];
      reviewIndexRef.current = 0;
      reviewModeRef.current = false;
      setIsReviewMode(false);
    }
  }, [getBasicDataFields, state.accountType, messages]);

  const handleInteractiveExitComplete = useCallback(() => {
    if (pendingSkillResetRef.current) {
      setSelectedCategoryFilter(null);
      pendingSkillResetRef.current = false;
    }
  }, [setSelectedCategoryFilter]);

  const lastTypingCompleteRef = useRef<string | null>(null);
  const handleTypingComplete = useCallback((messageId?: string) => {
    if (messageId && lastTypingCompleteRef.current === messageId) {
      return;
    }
    if (!messageId || messageId !== activeTypingMessageIdRef.current) {
      return;
    }
    lastTypingCompleteRef.current = messageId;
    activeTypingMessageIdRef.current = null;
    if (typingTimeoutRef.current !== null) {
      window.clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    const shouldDelaySummary = pendingQuestionRef.current?.interactiveType === 'summary';
    if (shouldDelaySummary) {
      window.setTimeout(() => {
        setIsTyping(false);
        setCompletedMessageId(messageId);
        pendingQuestionRef.current = null;
      }, 800);
      return;
    }
    setIsTyping(false);
    setCompletedMessageId(messageId);
    pendingQuestionRef.current = null;
  }, []);

  useEffect(() => {
    if (!isTyping) {
      if (typingTimeoutRef.current !== null) {
        window.clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      return;
    }
    const messageId = activeTypingMessageIdRef.current;
    if (!messageId || !lastAssistantMessage || lastAssistantMessage.id !== messageId) {
      return;
    }
    const duration = Math.max(1, lastAssistantMessage.content.length) * 30;
    if (typingTimeoutRef.current !== null) {
      window.clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = window.setTimeout(() => {
      handleTypingComplete(messageId);
    }, duration + 200);
  }, [isTyping, lastAssistantMessage, handleTypingComplete]);

  const handleReturnToCompleteActions = useCallback(() => {
    handleInteractiveAction('returnToCompleteActions');
  }, [handleInteractiveAction]);

  return {
    messages,
    currentQuestion,
    inputValue,
    setInputValue,
    handleSendMessage,
    handleInteractiveAction,
    handleSkipField,
    isProcessing,
    isTyping,
    navigateToStep,
    handleInteractiveExitComplete,
    isReviewMode,
    shouldShowSkip,
    skipLabel,
    handleTypingComplete,
    showReturnToFinish,
    handleReturnToCompleteActions,
  };
};
